import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { geocode } from "../loads/geo";

const cityCache = new Map(); // city -> Promise<{lat,lng}|null>
const cityPoint = (city) => {
  if (!cityCache.has(city)) cityCache.set(city, geocode(city).then((r) => (r ? { lat: r.lat, lng: r.lng } : null)));
  return cityCache.get(city);
};


const TONES = { free: ["#e6f8ee", "#0c8a43"], busy: ["#fff1e0", "#b25b00"], idle: ["#eef2f8", "#536b9c"] };

/** DOM node for the info window (text only — nothing is injected as HTML) */
function buildOne(info) {
  const el = (tag, css, text) => {
    const n = document.createElement(tag);
    if (css) n.style.cssText = css;
    if (text != null) n.textContent = text;
    return n;
  };
  const box = el("div", "font:12px/1.35 Inter,Arial,sans-serif;color:#060d23;min-width:0;width:100%;padding:0");
  const head = el("div", "display:flex;gap:8px;align-items:flex-start;justify-content:space-between;padding-right:18px");
  head.appendChild(el("strong", "font-size:13px;line-height:1.25", info.title));
  if (info.badge) {
    const [bg, fg] = TONES[info.badge.tone] || TONES.idle;
    head.appendChild(el("span", `flex:none;border-radius:99px;padding:2px 8px;font-size:11px;font-weight:600;background:${bg};color:${fg}`, info.badge.text));
  }
  box.appendChild(head);
  if (info.sub) box.appendChild(el("div", "color:#536b9c;margin-top:2px", info.sub));
  const list = el("div", "display:grid;gap:2px;margin-top:5px");
  (info.rows || []).forEach(([k, v]) => {
    const r = el("div", "display:grid;grid-template-columns:78px 1fr;gap:6px");
    r.appendChild(el("span", "color:#6479a3", k));
    r.appendChild(el("span", "font-weight:500;word-break:break-word", v));
    list.appendChild(r);
  });
  if (info.phone) {
    const r = el("div", "display:grid;grid-template-columns:78px 1fr;gap:6px");
    r.appendChild(el("span", "color:#6479a3", "Телефон"));
    const a = el("a", "color:#0063ff;font-weight:600;text-decoration:none", info.phone);
    a.href = `tel:${String(info.phone).replace(/[^\d+]/g, "")}`;
    r.appendChild(a);
    list.appendChild(r);
  }
  box.appendChild(list);
  (info.actions || []).forEach((a) => {
    const b = el("button", "margin-top:7px;width:100%;border:0;border-radius:8px;padding:7px 10px;font:600 12px Inter,Arial,sans-serif;background:#0063ff;color:#fff;cursor:pointer", a.label);
    b.type = "button";
    if (a.secondary) {
      b.style.background = "#eef4ff";
      b.style.color = "#0b4fc2";
    }
    if (a.disabled) {
      b.disabled = true;
      b.style.background = "#c9d6ec";
      b.style.color = "#fff";
      b.style.cursor = "default";
    } else {
      b.addEventListener("click", a.onClick);
    }
    box.appendChild(b);
  });
  return box;
}

/** one card, or a pager «‹ 2 / 3 ›» when several points share the same spot */
function buildCard(infos) {
  if (infos.length === 1) return buildOne(infos[0]);
  const wrap = document.createElement("div");
  const body = document.createElement("div");
  let i = 0;
  const nav = document.createElement("div");
  nav.style.cssText = "display:flex;align-items:center;justify-content:space-between;margin-bottom:6px;padding-right:18px;font:600 11px Inter,Arial,sans-serif;color:#536b9c";
  const label = document.createElement("span");
  const mk = (txt, d) => {
    const b = document.createElement("button");
    b.type = "button";
    b.textContent = txt;
    b.style.cssText = "border:1px solid #d5e0f2;background:#fff;border-radius:6px;width:26px;height:22px;cursor:pointer;font-size:14px;line-height:1";
    b.addEventListener("click", () => {
      i = (i + d + infos.length) % infos.length;
      show();
    });
    return b;
  };
  const show = () => {
    label.textContent = `${i + 1} из ${infos.length} на этой точке`;
    body.replaceChildren(buildOne(infos[i]));
  };
  const arrows = document.createElement("span");
  arrows.style.cssText = "display:flex;gap:4px";
  arrows.append(mk("‹", -1), mk("›", 1));
  nav.append(label, arrows);
  wrap.append(nav, body);
  show();
  return wrap;
}

/**
 * Real map for the wireframe's `.map-panel` raster (Google Maps JS API, loaded by AuthProvider).
 * `points`: [{lat, lng, city, title}] — rows without coordinates are placed by geocoding their city.
 * Imperative API for the wireframe controls: zoomIn / zoomOut / fit («Показать весь маршрут»).
 */
const TrucksMap = forwardRef(function TrucksMap({ points }, ref) {
  const el = useRef(null);
  const map = useRef(null);
  const markers = useRef([]);
  const bounds = useRef(null);
  const seq = useRef(0);
  const cardEl = useRef(null);
  const pts = useRef([]);
  const lastZoom = useRef(null);
  const routeObjs = useRef([]);

  /** design: dashed route line + red destination pin of the opened load (only when the load has coordinates) */
  const clearRoute = () => {
    routeObjs.current.forEach((o) => o.setMap(null));
    routeObjs.current = [];
  };
  const showRoute = (list) => {
    const g = window.google;
    clearRoute();
    const p = list.find((x) => x.to && x.to.lat != null && x.to.lng != null);
    if (!g || !g.maps || !p || !map.current) return;
    const line = new g.maps.Polyline({
      map: map.current,
      path: [
        { lat: p.lat, lng: p.lng },
        { lat: p.to.lat, lng: p.to.lng },
      ],
      strokeOpacity: 0,
      clickable: false,
      icons: [{ icon: { path: "M 0,-1 0,1", strokeOpacity: 1, strokeColor: "#0063ff", scale: 3 }, offset: "0", repeat: "12px" }],
    });
    const pin = new g.maps.Marker({
      map: map.current,
      position: { lat: p.to.lat, lng: p.to.lng },
      clickable: false,
      title: "Пункт назначения",
      icon: { path: g.maps.SymbolPath.CIRCLE, scale: 7, fillColor: "#ff3047", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2 },
    });
    routeObjs.current = [line, pin];
  };

  /** The dot's card is a panel over the map (not a map InfoWindow), so it is never clipped by a small map. */
  const closeCard = () => {
    clearRoute();
    if (cardEl.current) {
      cardEl.current.style.display = "none";
      cardEl.current.replaceChildren();
    }
  };
  const showCard = (infos) => {
    const box = cardEl.current;
    if (!box) return;
    const x = document.createElement("button");
    x.type = "button";
    x.setAttribute("aria-label", "Закрыть карточку");
    x.textContent = "×";
    x.style.cssText = "position:absolute;top:6px;right:8px;border:0;background:none;font-size:20px;line-height:1;color:#536b9c;cursor:pointer";
    x.addEventListener("click", closeCard);
    box.replaceChildren(x, buildCard(infos));
    box.scrollTop = 0;
    box.style.display = "block";
  };

  /** Draw dots for the current zoom: nearby points merge into a numbered cluster (click = zoom in);
   *  points on the very same spot stay one dot whose card pages through them. */
  const renderMarkers = () => {
    const g = window.google;
    if (!map.current || !g || !g.maps) return;
    const zoom = map.current.getZoom() || 4;
    lastZoom.current = zoom;
    markers.current.forEach((m) => m.setMap(null));
    markers.current = [];
    clearRoute();
    const world = 256 * 2 ** zoom;
    const cell = 52; // px
    const toXY = (p) => {
      const sin = Math.min(Math.max(Math.sin((p.lat * Math.PI) / 180), -0.9999), 0.9999);
      return [((p.lng + 180) / 360) * world, (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * world];
    };
    const cells = new Map();
    pts.current.forEach((p) => {
      const [x, y] = toXY(p);
      const key = `${Math.floor(x / cell)}:${Math.floor(y / cell)}`;
      if (!cells.has(key)) cells.set(key, []);
      cells.get(key).push(p);
    });
    cells.forEach((list) => {
      const lat = list.reduce((a, x) => a + x.lat, 0) / list.length;
      const lng = list.reduce((a, x) => a + x.lng, 0) / list.length;
      const p = list[0];
      const sameSpot = list.every((x) => Math.abs(x.lat - p.lat) < 0.0006 && Math.abs(x.lng - p.lng) < 0.0006);
      const stack = list.length > 1 && (sameSpot || zoom >= 15);
      const cluster = list.length > 1 && !stack;
      const infos = list.map((x) => x.info).filter(Boolean);
      const pos = stack || list.length === 1 ? { lat: p.lat, lng: p.lng } : { lat, lng };
      const marker = new g.maps.Marker({
        map: map.current,
        position: pos,
        title: list.length > 1 ? `${p.city || ""}: ${list.length}` : p.title || p.city || "",
        label: list.length > 1 ? { text: String(list.length), color: "#fff", fontSize: "12px", fontWeight: "700" } : undefined,
        icon: {
          path: g.maps.SymbolPath.CIRCLE,
          // design `.map-cluster`: solid blue disc, thin lighter-blue ring, white count
          scale: cluster ? Math.min(15 + String(list.length).length * 2, 22) : stack ? 11 : infos.length ? 8 : 7,
          fillColor: p.color || "#0063ff",
          fillOpacity: 1,
          strokeColor: cluster ? "#1681ff" : "#fff",
          strokeWeight: cluster ? 1 : 2,
        },
      });
      if (cluster) {
        marker.addListener("click", () => {
          const b = new g.maps.LatLngBounds();
          list.forEach((x) => b.extend({ lat: x.lat, lng: x.lng }));
          map.current.fitBounds(b, 70);
          g.maps.event.addListenerOnce(map.current, "idle", () => {
            if (map.current.getZoom() > 16) map.current.setZoom(16);
          });
        });
      } else if (infos.length) {
        marker.addListener("click", () => {
          showCard(infos);
          showRoute(list);
        });
      }
      markers.current.push(marker);
    });
  };

  const fit = () => {
    const g = window.google;
    if (map.current && g && bounds.current && !bounds.current.isEmpty()) {
      const ne = bounds.current.getNorthEast();
      const sw = bounds.current.getSouthWest();
      if (ne.equals(sw)) {
        map.current.setCenter(ne);
        map.current.setZoom(8);
        return;
      }
      map.current.fitBounds(bounds.current, 40);
      g.maps.event.addListenerOnce(map.current, "idle", () => {
        if (map.current.getZoom() > 9) map.current.setZoom(9);
      });
    }
  };

  useImperativeHandle(ref, () => ({
    zoomIn: () => map.current && map.current.setZoom(map.current.getZoom() + 1),
    zoomOut: () => map.current && map.current.setZoom(map.current.getZoom() - 1),
    fit,
  }));

  // keep tiles rendered when the container is resized / revealed (e.g. inside the dialog)
  useEffect(() => {
    if (!el.current || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => {
      const g = window.google;
      if (map.current && g && g.maps) {
        g.maps.event.trigger(map.current, "resize");
        fit();
      }
    });
    ro.observe(el.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    let tries = 0;
    let dead = false;
    const my = ++seq.current;
    const draw = async () => {
      const g = window.google;
      if (dead) return;
      if (!g || !g.maps || !el.current) {
        if (tries++ < 20) setTimeout(draw, 500);
        return;
      }
      if (!map.current) {
        map.current = new g.maps.Map(el.current, {
          center: { lat: 45, lng: 70 },
          zoom: 4,
          disableDefaultUI: true,
          clickableIcons: false,
          gestureHandling: "cooperative",
        });
      }
      const resolved = [];
      for (const p of points || []) {
        if (p.lat != null && p.lng != null) resolved.push({ ...p, lat: Number(p.lat), lng: Number(p.lng) });
        else if (p.city) {
          const c = await cityPoint(p.city);
          if (c) resolved.push({ ...p, ...c });
        }
        if (dead || my !== seq.current) return;
      }
      pts.current = resolved;
      closeCard();
      g.maps.event.clearListeners(map.current, "click");
      g.maps.event.clearListeners(map.current, "zoom_changed");
      map.current.addListener("click", closeCard);
      // clusters split as the user zooms in (grouping depends on the zoom level only, never on panning)
      map.current.addListener("zoom_changed", () => {
        if (map.current.getZoom() !== lastZoom.current) renderMarkers();
      });
      bounds.current = new g.maps.LatLngBounds();
      resolved.forEach((p) => bounds.current.extend({ lat: p.lat, lng: p.lng }));
      renderMarkers();
      fit();
    };
    draw();
    return () => {
      dead = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points]);

  return (
    <>
      <div ref={el} className="map-art" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", background: "#e5f3ed" }} />
      <div
        ref={cardEl}
        className="map-dot-card"
        style={{
          display: "none",
          position: "absolute",
          zIndex: 5,
          top: 10,
          left: 10,
          width: "min(300px, calc(100% - 20px))",
          maxHeight: "calc(100% - 20px)",
          overflow: "auto",
          boxSizing: "border-box",
          padding: "12px 14px",
          background: "#fff",
          border: "1px solid #dbe4f0",
          borderRadius: 12,
          boxShadow: "0 8px 28px #15304b33",
        }}
      />
    </>
  );
});

export default TrucksMap;
